<template>
	<div class="InputCheckbox" :class="classes">
		<input
			type="checkbox"
			:name="name"
			:value="value"
			:id="id + value"
			:checked="shouldBeChecked"
			@change="emitEvent"
			:disabled="disabled"
		/>
		<label :for="id + value">{{ label }}</label>
	</div>
</template>

<script>
export default {
	name: 'CloInputCheckbox',
	emits: ['change', 'update:checked'],
	data() {
		return {};
	},
	model: {
		prop: 'checked',
		event: 'change',
	},
	props: {
		name: {
			type: String,
			default: '',
		},
		value: {
			type: String,
			default: '',
		},
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		label: {
			type: String,
			default: '',
			required: true,
		},
		/**
		 * Meant to be used with v-model, can be boolean OR array of strings
		 */
		checked: {
			type: [Boolean, Array],
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		/**
		 * Size: sm, lg, xl
		 */
		size: {
			type: String,
			default: '',
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Style only - has no accessible or semantic meaning
		 */
		multiple: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			const classes = [];
			const prefix = 'InputCheckbox--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.size.length || this.reverse || this.multiple) {
				classes.push(prefix + 'styled');
			}
			if (this.multiple && !this.shouldBeChecked) {
				classes.push(prefix + 'multiple');
			}
			return classes.join(' ');
		},
		shouldBeChecked() {
			if (Array.isArray(this.checked)) {
				// multiple checkboxes
				return this.checked.includes(this.value);
			} else {
				// single boolean checkbox
				return this.checked;
			}
		},
	},
	methods: {
		emitEvent(e) {
			let isChecked = e.target.checked;
			if (Array.isArray(this.checked)) {
				// multiple checkboxes
				let newValue = [...this.checked];
				if (isChecked) {
					newValue.push(this.value);
				} else {
					newValue.splice(newValue.indexOf(this.value), 1);
				}
				this.$emit('change', newValue); // for use with v-model in Vue 2
				this.$emit('update:checked', newValue); // for use with v-model in Vue 3
			} else {
				// single boolean checkbox
				this.$emit('change', isChecked); // for use with v-model in Vue 2
				this.$emit('update:checked', isChecked); // for use with v-model in Vue 3
			}
		},
	},
};
</script>

<style lang="scss">
.InputCheckbox {
	display: block;
}

.InputCheckbox--styled {
	display: flex;
	@include stack-xs;
	label {
		@include text(3);
		margin: 0;
		&:before {
			box-sizing: border-box;
			content: '';
			display: inline-block;
			vertical-align: middle;
			height: 1rem;
			width: 1rem;
			border: 1px solid $color-form-outline;
			background-color: $color-white;
			@include inline-sm;
			border-radius: $radius-sm;
		}
	}
	// make it disappear
	[type='checkbox'] {
		appearance: none;
		position: absolute;
		opacity: 0;
		margin: 0;
	}
	[type='checkbox']:checked + label:before {
		background-color: $brand-color-ocean-blue-9;
		border-color: $color-form-outline-focus;
		background-repeat: no-repeat;
		background-position: center center;
		background-size: 85%;
		// our check icon, optimized, with white fill, and URL encoded
		background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%0A%20%20%3Cpath%20fill%3D%22%23fff%22%20d%3D%22M9.96%2019.725a.466.466%200%200%201-.616.042l-7.686-6.546a.41.41%200%200%201-.06-.59l2.76-3.1a.452.452%200%200%201%20.605-.042l4.208%203.588%208.387-9.434a.452.452%200%200%201%20.615-.042l3.175%202.694a.408.408%200%200%201%20.043.59L9.96%2019.726z%22%2F%3E%0A%3C%2Fsvg%3E');
	}
	[type='checkbox']:focus + label:before,
	[type='checkbox']:hover + label:before {
		box-shadow: 0 0 0 2px $color-form-outline-focus;
	}
	[type='checkbox'][disabled] + label,
	[type='checkbox'][readonly] + label {
		color: $color-neutral-3;
	}
	[type='checkbox'][disabled] + label:before,
	[type='checkbox'][readonly] + label:before {
		background-color: $color-neutral-1;
		border-color: $color-neutral-2;
	}
}

.InputCheckbox--reverse {
	label {
		color: $color-text-reverse;
		&:before {
			border: 1px solid $color-form-outline-reverse;
			background-color: $brand-color-sand-dark-1;
		}
	}
	[type='checkbox']:focus + label:before,
	[type='checkbox']:hover + label:before {
		box-shadow: 0 0 0 2px $color-form-outline-focus-reverse;
	}
	[type='checkbox'][disabled] + label,
	[type='checkbox'][readonly] + label {
		color: $color-neutral-3;
	}
	[type='checkbox'][disabled] + label:before,
	[type='checkbox'][readonly] + label:before {
		background-color: $color-neutral-3;
		border-color: $color-neutral-4;
		opacity: 0.5;
	}
}

.InputCheckbox--multiple {
	label:before,
	[type='checkbox']:focus + label:before {
		background-color: $color-white;
		border-color: $color-neutral-3;
		background-image: none;
		border-width: 4px;
		border-top-width: 7px;
		border-bottom-width: 7px;
	}
}

.InputCheckbox--xl {
	@include stack-md;
	label {
		@include text(5);
		&:before {
			height: 2rem;
			width: 2rem;
			@include inline-md;
		}
	}
	[type='checkbox']:checked + label:before {
		background-size: 70%;
	}
}

.InputCheckbox--lg {
	@include stack-sm;
	label {
		@include text(4);
		&:before {
			height: 1.25rem;
			width: 1.25rem;
		}
	}
	[type='checkbox']:checked + label:before {
		background-size: 80%;
	}
}

.InputCheckbox--sm {
	//margin: 0;
	label {
		@include text(2);
	}
}
</style>
